import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { type ComponentProps, forwardRef, memo, type PointerEvent, useCallback, useMemo, useRef } from 'react';

import { cn } from '@air/utils-tailwind';

import { usePlayerSeekChange } from '../hooks/usePlayerSeekChange';
import { usePlayerStore } from './PlayerContextProvider';

export type PlayerSeekbarProps = ComponentProps<'div'>;

export const PlayerSeekbar = memo(
  forwardRef<HTMLDivElement, PlayerSeekbarProps>(
    (
      { children, className, onPointerMove, onPointerDown, onPointerUp, onPointerCancel, ...restOfProps },
      forwardedRef,
    ) => {
      const { onPlayerSeekChange, onPlayerSeekEnd, onPlayerSeekStart } = usePlayerSeekChange();
      const currentTime = usePlayerStore((state) => state.currentTime);
      const duration = usePlayerStore((state) => state.duration);
      const containerRef = useRef<HTMLDivElement>(null);
      const isDraggingRef = useRef(false);
      const composedRefs = useComposedRefs(forwardedRef, containerRef);

      const handleSeek = useCallback(
        (clientX: number) => {
          if (!containerRef.current || duration <= 0) {
            return;
          }

          const rect = containerRef.current.getBoundingClientRect();
          const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
          const newTime = percentage * duration;

          onPlayerSeekChange(newTime);
        },
        [duration, onPlayerSeekChange],
      );

      const handlePointerDown = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          if (!isDraggingRef.current) {
            isDraggingRef.current = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            onPlayerSeekStart();
            handleSeek(event.clientX);
          }

          onPointerDown?.(event);
        },
        [handleSeek, onPlayerSeekStart, onPointerDown],
      );

      const handlePointerMove = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          if (isDraggingRef.current) {
            handleSeek(event.clientX);
          }

          onPointerMove?.(event);
        },
        [handleSeek, onPointerMove],
      );

      const handlePointerUp = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          if (isDraggingRef.current) {
            event.currentTarget.releasePointerCapture(event.pointerId);
            isDraggingRef.current = false;
            onPlayerSeekEnd();
          }

          onPointerUp?.(event);
        },
        [onPlayerSeekEnd, onPointerUp],
      );

      const handlePointerCancel = useCallback(
        (event: PointerEvent<HTMLDivElement>) => {
          handlePointerUp(event);

          onPointerCancel?.(event);
        },
        [handlePointerUp, onPointerCancel],
      );

      const progress = useMemo(() => {
        if (duration <= 0) {
          return 0;
        }

        return (currentTime / duration) * 100;
      }, [currentTime, duration]);

      return (
        <div
          className={cn(
            'group/seekbar relative flex size-full shrink-0 cursor-pointer select-none items-end',
            className,
          )}
          data-progress={progress}
          data-testid="PLAYER_SEEKBAR"
          onPointerCancel={handlePointerCancel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          {...restOfProps}
          ref={composedRefs}
        >
          {children}
        </div>
      );
    },
  ),
);

PlayerSeekbar.displayName = 'PlayerSeekbar';
